/** @format */

import { memo } from 'react'

import styles from 'dir/styles/courses.module.css'

export const _4 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>4 - Псевдоклассы и псевдоэлементы</h2>
    <p className={styles.description}>
      Используются для выбора определенных элементов или состояний в коде HTML без присваивания идентификаторов или
      классов, а также для создания элементов, отсутствующих в html документе.
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.1 - Псевдоэлементы</h3>
      <p className={styles.description}>
        Псевдоэлементы <code className={styles.code}>::before</code> и <code className={styles.code}>::after</code>{' '}
        позволяют обходиться без создания в коде HTML тегов, не содержащих контент, а служащих только для оформления.
        Например, можно стилизовать список, не изменяя HTML верстку.{' '}
      </p>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Псевдоэлемент</th>
            <th>Назначение</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>::after</td>
            <td>создает последнего потомка соответствующего элемента. Дополняется свойством content: ''</td>
          </tr>
          <tr>
            <td>::before</td>
            <td>создает первого потомка соответствующего элемента. Дополняется свойством content: ''</td>
          </tr>
          <tr>
            <td>::first-letter</td>
            <td>для стилизации первой буквы текста</td>
          </tr>
          <tr>
            <td>::first-line</td>
            <td>для стилизации первой строки текста</td>
          </tr>
          <tr>
            <td>::selection</td>
            <td>для стилизации текста, выделенного пользователем. Всегда должен записываться с двойным двоеточием.</td>
          </tr>
        </tbody>
      </table>
      <p className={styles.notificationWarn}>
        Спецсимволы в свойстве <code className={styles.code}>content</code> псевдоэлементов{' '}
        <code className={styles.code}>before</code> или <code className={styles.code}>after</code> следует записывать в
        виде ASCII последовательности, а не hex последовательности.{' '}
      </p>
      <p className={styles.description}>
        В селекторе можно использовать <b>только один</b> псевдоэлемент. Он должен добавляться после простого селектора
        в выражении.{' '}
      </p>
      <p className={styles.description}>
        Некоторым элементам невозможно присвоить класс или идентификатор, например первой строке или символу блока,
        изменяющего свою ширину. Псевдоэлементы <code className={styles.code}>first-line</code> и{' '}
        <code className={styles.code}>first-letter</code> решают подобную задачу.{' '}
      </p>
      <p className={styles.description}>
        Некоторые элементы должны изменять свой вид при изменении состояния, например при нахождении курсора в элементе
        input или при наведении указателем мыши. Псевдоклассы <code className={styles.code}>:focus</code> или{' '}
        <code className={styles.code}>:hover</code> используются в подобных ситуациях - наведите курсор на зеленое поле
        или белый круг в примере:{' '}
      </p>
      <p className={styles.description}>
        Некоторые элементы имеют такое положение среди других элементов, которое требует изменить их вид, например
        первый, последний или n-й элемент списка. Псевдоклассы для выделения потомков или соседей, подобные{' '}
        <code className={styles.code}>:first-child</code>
        или <code className={styles.code}>:nth-child</code> помогают создавать селекторы положения.
      </p>
      <p className={styles.notificationWarn}>
        Программы для чтения с экрана не имеют доступ к содержимому, созданному с использованием псевдоэлементов и не
        могут его прочитать, поэтому рекомендуется не использовать псевдоэлементы для вставки важного контента на
        страницу.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.2 - Псевдоклассы состояния</h3>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Псевдокласс</th>
            <th>Назначение</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>:checked</td>
            <td>для стилизации выбранного/включенного input type='radio', type='checkbox' или option внутри select</td>
          </tr>
          <tr>
            <td>:disabled</td>
            <td>выбирает любой отключенный элемент</td>
          </tr>
          <tr>
            <td>:focus</td>
            <td>
              для стилизации элемента, в котором находится курсор (фокус можно получить при переходе с помощью клавиши
              tab и мышью)
            </td>
          </tr>
          <tr>
            <td>:hover</td>
            <td>для стилизации при наведении указателем мыши (но не обязательно активации)</td>
          </tr>
          <tr>
            <td>:target</td>
            <td>для стилизации элемента, выбранного с помощью ссылки</td>
          </tr>
        </tbody>
      </table>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.3 - Структурные псевдоклассы</h3>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Псевдокласс</th>
            <th>Назначение</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>:root</td>
            <td>выбирает корневой элемент (для HTML это тег html)</td>
          </tr>
          <tr>
            <td>:first-child</td>
            <td>выбирает первого потомка у родителя селектора</td>
          </tr>
          <tr>
            <td>:last-child</td>
            <td>выбирает последнего потомка у родителя селектора</td>
          </tr>
          <tr>
            <td>:first-of-type</td>
            <td>выбирает первый селектор данного типа</td>
          </tr>
          <tr>
            <td>:last-of-type</td>
            <td>выбирает последний селектор данного типа</td>
          </tr>
          <tr>
            <td>:nth-child()</td>
            <td>
              выбирает заданный в кавычках номер потомка селектора, используется цикл{' '}
              <code className={styles.code}>an+b</code> или ключевые слова <code className={styles.code}>even</code> и{' '}
              <code className={styles.code}>odd</code>
            </td>
          </tr>
          <tr>
            <td>:nth-last-child()</td>
            <td>аналог :nth-child() c отличием в том, что отсчет ведется с последнего элемента</td>
          </tr>
          <tr>
            <td>:nth-of-type()</td>
            выбирает заданный в кавычках номер селектора, используется цикл <code className={styles.code}>
              an+b
            </code>{' '}
            или ключевые слова <code className={styles.code}>even</code> и <code className={styles.code}>odd</code>
          </tr>
          <tr>
            <td>:nth-last-of-type()</td>
            <td>аналог :nth-of-type() с отличием в том, что отсчет ведется с последнего элемента</td>
          </tr>
          <tr>
            <td>:empty</td>
            <td>выбирает элементы без потомков и текста</td>
          </tr>
          <tr>
            <td>:only-of-type</td>
            <td>
              выбирает элементы любого типа у потомков данного селектора по признаку наличия единственного экземпляра
              (уникальности)
            </td>
          </tr>
          <tr>
            <td>:only-child</td>
            <td>
              выбирает элементы любого типа у потомков данного селектора по признаку отсутствия соседних элементов
            </td>
          </tr>
        </tbody>
      </table>
      <section className={styles.subsection}>
        <h3 className={styles.h4}>4.3.1 - Цикл an+b</h3>
        <p className={styles.description}>
          Используется в таких псевдоклассах как <code className={styles.code}>nth-child</code>,{' '}
          <code className={styles.code}>nth-last-child</code> и их вариациях. Этот цикл позволяет задать правило для
          сложной последовательности элементов, приведенной в примерах ниже. Начнем с описания синтаксиса записи цикла:{' '}
        </p>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <code className={styles.code}>a</code> - произвольное число, определяет период цикла
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>n</code> - счетчик цикла, начинается с нуля
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>b</code> - произвольное число, определяет смещение
          </li>
        </ul>
        <a
          className={styles.link}
          href="https://css-tricks.com/useful-nth-child-recipies/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Полезные nth-child рецепты
        </a>
      </section>
    </section>
  </section>
))
